/**
 * FloatingChatWidget — 모든 대시보드 페이지(챗봇 페이지 제외) 우측 하단의 AI Chat 버튼 + 인라인 챗봇.
 *
 * - 메인 챗봇과 동일한 백엔드(/chat SSE) · 동일 quota(_Feature.CHAT) 공유
 * - AI 모델 선택 (gemini / local — local 은 Enterprise 전용, 메인 챗봇과 동일 규칙)
 * - "현재 화면 참고" 토글 — 사용자가 작성 중인 폼 내용을 수집(pageContext)해 답변에 활용
 * - 대화/열림 상태는 sessionStorage 에 저장 → 페이지 이동해도 유지
 *
 * dev 전용으로 먼저 구현. (운영 반영은 release.sh)
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { FiMessageCircle, FiX, FiSend, FiTrash2, FiFileText, FiMinus, FiMaximize2 } from 'react-icons/fi'
import { cn } from '../../lib/utils'
import { openSSEStream } from '../../utils/sseStream'
import { useQuotaGuard } from '../../hooks/useQuotaGuard'
import { useAuthStore } from '../../store/authStore'
import { useThemeStore } from '../../store/themeStore'
import { toast } from '../../hooks/useToast'
import { collectPageContext } from '../../lib/pageContext'
import MarkdownView from './MarkdownView'
import UpgradeModal from './UpgradeModal'
import Spinner from '../ui/Spinner'

interface Msg { id: string; role: 'user' | 'assistant'; content: string; status?: string; streaming?: boolean }

const SS_MSGS = 'heinrich.floatingChat.msgs.v1'
const SS_MODEL = 'heinrich.floatingChat.model'
const SS_PAGECTX = 'heinrich.floatingChat.pagectx'

function uid() { return Math.random().toString(36).slice(2, 10) }

function loadMsgs(): Msg[] {
  try {
    const raw = sessionStorage.getItem(SS_MSGS)
    if (!raw) return []
    const arr = JSON.parse(raw) as Msg[]
    return Array.isArray(arr) ? arr.filter((m) => m && (m.role === 'user' || m.role === 'assistant')) : []
  } catch { return [] }
}

export default function FloatingChatWidget() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const { user } = useAuthStore()
  const { mode: themeMode } = useThemeStore()
  const { quota, handleQuotaError, closeQuota } = useQuotaGuard()
  const isEnterprise = user?.plan_code === 'enterprise'

  // open=패널 표시 여부 (페이지 이동 시 닫힘 — 영속화 안 함). minimized=슬림 바만 표시.
  const [open, setOpen] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [msgs, setMsgs] = useState<Msg[]>(loadMsgs)
  const [input, setInput] = useState('')
  const [model, setModel] = useState<'gemini' | 'local'>(() => {
    const m = sessionStorage.getItem(SS_MODEL)
    return m === 'local' ? 'local' : 'gemini'
  })
  const [usePageCtx, setUsePageCtx] = useState<boolean>(() => sessionStorage.getItem(SS_PAGECTX) !== '0')
  const [isStreaming, setIsStreaming] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  // ── 챗봇 페이지에서는 위젯 숨김 ──
  const hidden = location.pathname.startsWith('/dashboard/chatbot') || location.pathname.startsWith('/dashboard/chat-studio')

  // 페이지 이동 시 자연스럽게 닫기 (대화 내용은 sessionStorage 에 남음 — 다시 열면 이어짐)
  useEffect(() => { setOpen(false); setMinimized(false) }, [location.pathname])

  useEffect(() => { try { sessionStorage.setItem(SS_MSGS, JSON.stringify(msgs.slice(-30))) } catch { /* ignore */ } }, [msgs])
  useEffect(() => { sessionStorage.setItem(SS_MODEL, model) }, [model])
  useEffect(() => { sessionStorage.setItem(SS_PAGECTX, usePageCtx ? '1' : '0') }, [usePageCtx])
  useEffect(() => {
    if (open && !minimized && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [open, minimized, msgs.length, isStreaming])
  useEffect(() => () => { abortRef.current?.abort() }, [])

  const setMsg = useCallback((id: string, patch: Partial<Msg>) => {
    setMsgs((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)))
  }, [])

  const send = useCallback(async () => {
    const q = input.trim()
    if (!q || isStreaming) return
    setInput('')

    const userMsg: Msg = { id: uid(), role: 'user', content: q }
    const asstId = uid()
    const asstMsg: Msg = { id: asstId, role: 'assistant', content: '', streaming: true, status: t('floatingChat.status.searching', { defaultValue: '검색 중...' }) }
    setMsgs((prev) => [...prev, userMsg, asstMsg])
    setIsStreaming(true)

    // 대화 히스토리 (최근 user/assistant 페어 — 최대 6개) + 화면 컨텍스트
    const priorTurns = msgs
      .filter((m) => m.content.trim())
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.content.slice(0, 1200) }))
    const history: { role: string; content: string }[] = []
    const isEn = i18n.language?.startsWith('en')
    if (usePageCtx) {
      const ctx = collectPageContext()
      if (ctx) {
        const sysNote = isEn
          ? 'The user is filling out an occupational safety document. Below is the current state of the form — ' +
            'for each field, "selected" / "available (unselected)" options and any entered values.\n' +
            'When answering, you must:\n' +
            '① Review the user\'s current selections/inputs first.\n' +
            '② From the "available (unselected)" options, suggest specific items that should also be ' +
            'selected for safety or legal reasons, quoting each option label exactly. ' +
            '(e.g., "Since you selected Confined Workspace, also check \'Enclosed Space\'", ' +
            '"For coating work, also check \'Hazardous Chemical Handling\'")\n' +
            '③ Prioritize concrete advice based on the actual on-screen option labels — what else to ' +
            'select or fill in — over generic safety information.\n\n' +
            ctx
          : '사용자가 산업안전 문서를 작성 중입니다. 아래는 현재 화면의 입력 상태 — 항목별로 ' +
            '"선택됨"과 "선택 가능(미선택)" 목록, 그리고 입력값을 정리한 것입니다.\n' +
            '답변할 때 반드시:\n' +
            '① 사용자의 현재 선택/입력을 먼저 검토하고,\n' +
            '② 위 "선택 가능(미선택)" 목록 중에서 안전상·법적으로 추가로 선택하면 좋은 항목을 ' +
            '그 항목명을 그대로 인용해 구체적으로 제안하세요. ' +
            '(예: "협소공간을 선택하셨다면 \'밀폐공간\' 항목도 함께 체크하세요", "도장·코팅 작업이면 \'유해화학물질 취급\' 항목 추가 권장")\n' +
            '③ 화면에 없는 일반론보다, 화면의 실제 옵션 라벨을 기준으로 "무엇을 더 선택/입력해야 하는지" 를 우선 안내하세요.\n\n' +
            ctx
        history.push({ role: 'user', content: sysNote })
        history.push({
          role: 'assistant',
          content: isEn
            ? 'Understood — I\'ll review the selected and available options on the screen and suggest additional ones to check.'
            : '네, 현재 화면의 선택됨/선택 가능 항목을 검토하고 추가로 선택하면 좋을 항목을 제안하겠습니다.',
        })
      }
    }
    history.push(...priorTurns)

    const params = new URLSearchParams({ query: q, model, lang: isEn ? 'en' : 'ko' })
    const abort = new AbortController()
    abortRef.current = abort
    let accumulated = ''

    try {
      await openSSEStream(
        `/chat?${params}`,
        {
          onEvent: (event, data) => {
            if (event === 'chat_chunk') {
              let chunk = data
              try { const p = JSON.parse(data) as { text?: string }; if (typeof p?.text === 'string') chunk = p.text } catch { /* raw */ }
              accumulated += chunk
              setMsg(asstId, { content: accumulated, status: undefined })
            } else if (event === 'chat_status') {
              let s = data
              try { const p = JSON.parse(data) as { message?: string }; if (typeof p?.message === 'string') s = p.message } catch { /* raw */ }
              setMsg(asstId, { status: s })
            } else if (event === 'chat_refs') {
              try {
                const refs = JSON.parse(data) as { cases?: unknown[]; laws?: unknown[] }
                const n = (refs.cases?.length ?? 0) + (refs.laws?.length ?? 0)
                if (n > 0) setMsg(asstId, { status: t('floatingChat.status.refsN', { count: n, defaultValue: '참고 자료 {{count}}건' }) })
              } catch { /* ignore */ }
            } else if (event === 'chat_complete') {
              setMsg(asstId, { streaming: false, status: undefined })
            } else if (event === 'demo_limit') {
              handleQuotaError(data)
              setMsg(asstId, { streaming: false, status: undefined, content: accumulated || t('floatingChat.error.quotaReached', { defaultValue: '사용량 한도에 도달했습니다.' }) })
            } else if (event === 'chat_error') {
              toast.error(t('floatingChat.error.generation', { defaultValue: '답변 생성 중 오류가 발생했습니다.' }))
              setMsg(asstId, { streaming: false, status: undefined, content: accumulated || t('floatingChat.error.generic', { defaultValue: '오류가 발생했습니다.' }) })
            }
          },
          onError: (err) => {
            if (!handleQuotaError(err)) toast.error(t('floatingChat.error.connection', { defaultValue: '챗봇 연결에 실패했습니다.' }))
            setMsg(asstId, { streaming: false, status: undefined, content: accumulated || t('floatingChat.error.connectionMsg', { defaultValue: '연결 오류가 발생했습니다.' }) })
            setIsStreaming(false)
          },
          onDone: () => { setMsg(asstId, { streaming: false, status: undefined }); setIsStreaming(false) },
        },
        abort.signal,
        history.length > 0 ? { body: { history } } : undefined,
      )
    } catch {
      setMsg(asstId, { streaming: false, status: undefined, content: accumulated || t('floatingChat.error.generic', { defaultValue: '오류가 발생했습니다.' }) })
      setIsStreaming(false)
    }
  }, [input, isStreaming, msgs, model, usePageCtx, setMsg, handleQuotaError, t])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send() }
  }

  const clearChat = () => {
    abortRef.current?.abort()
    setIsStreaming(false)
    setMsgs([])
    sessionStorage.removeItem(SS_MSGS)
  }

  if (hidden) return null

  const mdVariant = themeMode === 'light' ? 'light' : 'dark'

  return (
    <>
      {/* 닫힘 — 플로팅 버튼 */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            initial={{ opacity: 0, scale: 0.7, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 12 }}
            transition={{ type: 'spring', stiffness: 420, damping: 26 }}
            style={{ transformOrigin: 'bottom right' }}
            onClick={() => { setOpen(true); setMinimized(false) }}
            className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full
                       bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold text-sm shadow-md
                       transition-colors"
            aria-label={t('floatingChat.aria.open', { defaultValue: 'AI Chat 열기' })}
          >
            <FiMessageCircle size={18} />
            <span className="hidden sm:inline">{t('floatingChat.fab.label', { defaultValue: 'AI Chat' })}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* 최소화 — 슬림 바 (페이지의 다른 버튼을 가리지 않도록 작게) */}
      <AnimatePresence>
        {open && minimized && (
          <motion.button
            key="mini"
            initial={{ opacity: 0, scale: 0.8, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 12 }}
            transition={{ type: 'spring', stiffness: 420, damping: 26 }}
            style={{ transformOrigin: 'bottom right' }}
            onClick={() => setMinimized(false)}
            className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full
                       bg-surface-card/85 backdrop-blur-lg border border-surface-elevated/50 text-ink text-sm font-medium shadow-md
                       hover:bg-surface-card transition-colors"
            aria-label={t('floatingChat.aria.expand', { defaultValue: 'AI Chat 펼치기' })}
          >
            <FiMessageCircle size={16} className="text-brand-primary-light" />
            {t('floatingChat.fab.label', { defaultValue: 'AI Chat' })}
            {isStreaming && <Spinner size="sm" />}
            <FiMaximize2 size={13} className="text-ink-faint" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* 열림 — 챗 패널 (반투명: 뒤 페이지 윤곽이 살짝 비침) */}
      <AnimatePresence>
      {open && !minimized && (
        <motion.div
          key="panel"
          data-floating-chat
          initial={{ opacity: 0, y: 28, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 28, scale: 0.94 }}
          transition={{ type: 'spring', stiffness: 320, damping: 30, mass: 0.85 }}
          style={{ transformOrigin: 'bottom right' }}
          className="fixed bottom-5 right-5 z-40 w-[min(360px,92vw)] h-[min(520px,72vh)]
                     flex flex-col rounded-lg bg-surface-card/82 backdrop-blur-lg border border-surface-elevated/50 shadow-md overflow-hidden"
        >
          {/* 헤더 */}
          <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-surface-elevated/40 bg-surface-elevated/15">
            <FiMessageCircle size={16} className="text-brand-primary-light shrink-0" />
            <span className="text-sm font-semibold text-ink flex-1 truncate">{t('floatingChat.title', { defaultValue: 'AI 안전 도우미' })}</span>
            <select
              value={model}
              onChange={(e) => {
                const next = e.target.value as 'gemini' | 'local'
                if (next === 'local' && !isEnterprise) { toast.error(t('floatingChat.error.localEnterpriseOnly', { defaultValue: 'Local 모델은 Enterprise 플랜 전용입니다.' })); return }
                setModel(next)
              }}
              className="text-[11px] bg-surface-card/70 border border-surface-elevated/50 rounded px-1.5 py-1 text-ink focus:outline-none"
              title={!isEnterprise ? t('floatingChat.tooltip.localEnterpriseOnly', { defaultValue: 'Local 모델은 Enterprise 전용' }) : undefined}
            >
              <option value="gemini">Gemini</option>
              <option value="local">Local{!isEnterprise ? ' (Ent)' : ''}</option>
            </select>
            <button onClick={clearChat} className="text-ink-faint hover:text-ink p-1" title={t('floatingChat.tooltip.clearChat', { defaultValue: '대화 비우기' })} aria-label={t('floatingChat.aria.clearChat', { defaultValue: '대화 비우기' })}>
              <FiTrash2 size={14} />
            </button>
            <button onClick={() => setMinimized(true)} className="text-ink-faint hover:text-ink p-1" title={t('floatingChat.tooltip.minimize', { defaultValue: '최소화 (페이지 조작용)' })} aria-label={t('floatingChat.aria.minimize', { defaultValue: '최소화' })}>
              <FiMinus size={16} />
            </button>
            <button onClick={() => setOpen(false)} className="text-ink-faint hover:text-ink p-1" title={t('floatingChat.tooltip.close', { defaultValue: '닫기' })} aria-label={t('floatingChat.aria.close', { defaultValue: '닫기' })}>
              <FiX size={16} />
            </button>
          </div>

          {/* 메시지 목록 */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {msgs.length === 0 ? (
              <div className="text-center text-xs text-ink-faint mt-8 px-4 leading-relaxed space-y-1.5">
                <p>{t('floatingChat.empty.intro1', { defaultValue: '문서를 작성하다 궁금한 점을 물어보세요.' })}<br />{t('floatingChat.empty.intro2', { defaultValue: '산업안전법령·KOSHA 가이드·사고사례 기반으로 답변합니다.' })}</p>
                <p className="text-ink-muted">{t('floatingChat.empty.pageCtxPrefix', { defaultValue: '"현재 화면 참고" 가 켜져 있으면 지금 작성 중인 항목과 선택 가능한 항목을 보고,' })} <b className="text-ink-muted">{t('floatingChat.empty.pageCtxBold', { defaultValue: '추가로 선택하면 좋을 항목' })}</b>{t('floatingChat.empty.pageCtxSuffix', { defaultValue: '을 제안해 드립니다.' })}</p>
                <p className="text-ink-faint/80">{t('floatingChat.empty.example', { defaultValue: '예: "이 작업허가서에 추가할 작업 환경/조건 있을까?"' })}</p>
              </div>
            ) : (
              msgs.map((m) =>
                m.role === 'user' ? (
                  <div key={m.id} className="flex justify-end">
                    <div className="max-w-[85%] rounded-lg rounded-br-sm bg-brand-primary/15 text-ink px-3 py-2 text-[13px] whitespace-pre-wrap break-words">
                      {m.content}
                    </div>
                  </div>
                ) : (
                  <div key={m.id} className="flex justify-start">
                    <div className="max-w-[92%] rounded-lg rounded-bl-sm bg-surface-elevated/30 px-3 py-2">
                      {m.content
                        ? <MarkdownView size="sm" variant={mdVariant}>{m.content}</MarkdownView>
                        : (
                          <div className="flex items-center gap-2 text-[12px] text-ink-muted">
                            <Spinner size="sm" /> {m.status || t('floatingChat.status.thinking', { defaultValue: '생각 중...' })}
                          </div>
                        )}
                      {m.content && m.streaming && m.status && (
                        <div className="mt-1 text-[10px] text-ink-faint">{m.status}</div>
                      )}
                    </div>
                  </div>
                ),
              )
            )}
          </div>

          {/* 입력 */}
          <div className="border-t border-surface-elevated/40 p-2.5 space-y-1.5">
            <label className="flex items-center gap-1.5 text-[10px] text-ink-faint cursor-pointer select-none">
              <input type="checkbox" checked={usePageCtx} onChange={(e) => setUsePageCtx(e.target.checked)} className="accent-brand-primary" />
              <FiFileText size={10} /> {t('floatingChat.label.usePageCtx', { defaultValue: '현재 화면 참고 (작성 중인 내용을 답변에 활용)' })}
            </label>
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={t('floatingChat.askPlaceholder', { defaultValue: '질문을 입력하세요 (Enter 전송, Shift+Enter 줄바꿈)' })}
                className="flex-1 resize-none bg-surface-card/70 border border-surface-elevated/50 rounded-lg px-2.5 py-2 text-[13px] text-ink
                           focus:outline-none focus:ring-1 focus:ring-brand-primary max-h-24"
                disabled={isStreaming}
              />
              <button
                onClick={() => void send()}
                disabled={isStreaming || !input.trim()}
                className={cn(
                  'shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors',
                  isStreaming || !input.trim()
                    ? 'bg-surface-elevated/40 text-ink-faint cursor-not-allowed'
                    : 'bg-brand-primary hover:bg-brand-primary/90 text-white',
                )}
                aria-label={t('floatingChat.aria.send', { defaultValue: '전송' })}
              >
                {isStreaming ? <Spinner size="sm" /> : <FiSend size={15} />}
              </button>
            </div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* quota 초과 모달 (메인 챗봇과 동일) */}
      <UpgradeModal isOpen={quota.open} onClose={closeQuota} feature={quota.feature || 'chat'} quotaInfo={quota.info} />
    </>
  )
}
